/**
 * Layout principal de l'application
 * Sidebar + contenu principal
 */

import { useState, useEffect, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    FileText,
    ArrowDownCircle,
    Calculator,
    Settings,
    LogOut,
    Menu,
    X,
    ChevronRight as _ChevronRight,
    Users,
    ClipboardList,
    Briefcase,
    BookOpen,
    HardHat,
    Bell,
    Landmark,
    MailWarning,
    MessageSquare,
    Shield,
    Inbox,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useRealtimeEvents } from '@/hooks/useRealtimeEvents';
import { useSubscription } from '@/hooks/useSubscription';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { TrialBanner } from '@/components/TrialBanner';
import { SystemBanner } from '@/components/SystemBanner';
import { SubscriptionBlockedOverlay } from '@/components/SubscriptionBlockedOverlay';
import { PricingModal } from '@/components/PricingModal';
import { FeedbackButton } from '@/components/FeedbackButton';
import { InstallAppPrompt } from '@/components/InstallAppPrompt';
import { DemoBanner } from '@/components/DemoBanner';
import { CoachmarkProvider } from '@/components/coachmarks';
import { Button } from '@/components/ui';
import type { User } from '@/types';

// ========================================
// Navigation Structure
// ========================================

interface NavItem {
    to: string;
    icon: LucideIcon;
    label: string;
    module?: keyof Pick<User, 'moduleCatalogue' | 'moduleSousTraitance' | 'moduleRelances'>;
}

interface NavGroup {
    label?: string;
    items: NavItem[];
}

const navGroups: NavGroup[] = [
    {
        items: [
            { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        ],
    },
    {
        label: 'Commercial',
        items: [
            { to: '/clients', icon: Users, label: 'Clients' },
            { to: '/quotes', icon: ClipboardList, label: 'Devis' },
            { to: '/invoices', icon: FileText, label: 'Factures' },
            { to: '/inbound-invoices', icon: Inbox, label: 'Factures reçues' },
        ],
    },
    {
        label: 'Chantiers',
        items: [
            { to: '/projects', icon: Briefcase, label: 'Chantiers' },
            { to: '/expenses', icon: ArrowDownCircle, label: 'Dépenses' },
            { to: '/bank', icon: Landmark, label: 'Banque' },
            { to: '/retentions', icon: Shield, label: 'Retenues' },
            { to: '/relances', icon: Bell, label: 'Relances', module: 'moduleRelances' },
        ],
    },
    {
        label: 'Outils',
        items: [
            { to: '/catalogue', icon: BookOpen, label: 'Catalogue', module: 'moduleCatalogue' },
            { to: '/subcontractors', icon: HardHat, label: 'Sous-traitants', module: 'moduleSousTraitance' },
            { to: '/simulator', icon: Calculator, label: 'Simulateur' },
        ],
    },
];

// ========================================
// Page titles (mobile header)
// ========================================

const PAGE_TITLES: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/clients': 'Clients',
    '/quotes': 'Devis',
    '/invoices': 'Factures',
    '/inbound-invoices': 'Factures reçues',
    '/projects': 'Chantiers',
    '/expenses': 'Dépenses',
    '/bank': 'Banque',
    '/retentions': 'Retenues de garantie',
    '/relances': 'Relances',
    '/catalogue': 'Catalogue',
    '/subcontractors': 'Sous-traitants',
    '/simulator': 'Simulateur',
    '/settings': 'Paramètres',
};

// ========================================
// Layout Component
// ========================================

interface LayoutProps {
    children: ReactNode;
}

export function Layout({ children }: LayoutProps): ReactNode {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const pageTitle = PAGE_TITLES[location.pathname] ?? '';

    // Real-time cache invalidation via SSE
    useRealtimeEvents();

    // Scroll to top on page navigation
    useScrollToTop();

    const subscription = useSubscription(user);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [blockedCode, setBlockedCode] = useState<string | undefined>(undefined);
    const [showBlocked, setShowBlocked] = useState(false);
    const [showEmailBanner, setShowEmailBanner] = useState(false);
    const [showPricing, setShowPricing] = useState(false);

    // Écouter les événements 402 du response interceptor
    useEffect(() => {
        const handler = (e: Event) => {
            const code = (e as CustomEvent).detail?.code;
            setBlockedCode(code);
            setShowBlocked(true);
        };
        window.addEventListener('subscription:blocked', handler);
        return () => window.removeEventListener('subscription:blocked', handler);
    }, []);

    // Écouter l'événement email non vérifié (403 EMAIL_NOT_VERIFIED)
    useEffect(() => {
        const handler = () => setShowEmailBanner(true);
        window.addEventListener('auth:email-not-verified', handler);
        return () => window.removeEventListener('auth:email-not-verified', handler);
    }, []);

    // Overlay affiché soit sur événement 402 (showBlocked), soit si déjà bloqué au chargement
    const blocked = showBlocked || subscription.urgency === 'expired';

    // Lock body scroll when mobile drawer is open (prevents background scroll/chaining)
    useEffect(() => {
        if (!sidebarOpen) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [sidebarOpen]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <>
            {blocked && <SubscriptionBlockedOverlay code={blockedCode} />}
            {showEmailBanner && (
                <div className="fixed top-0 left-0 right-0 z-60 bg-[#F59E0B] text-white px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] flex items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-2">
                        <MailWarning className="h-4 w-4 shrink-0" />
                        <span>Confirmez votre adresse email pour accéder à toutes les fonctionnalités.</span>
                    </div>
                    <button onClick={() => setShowEmailBanner(false)} className="shrink-0 hover:opacity-75 flex items-center justify-center h-11 w-11 -my-2">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0F172A]">
                {/* Mobile Header */}
                <header className="lg:hidden fixed top-0 left-0 right-0 h-[calc(3.5rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-white dark:bg-[#1E293B] border-b border-[#E2E8F0] dark:border-[#334155] z-40 pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] flex items-center gap-2">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="h-11 w-11 -ml-2 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] dark:hover:bg-[#334155] transition-colors shrink-0"
                        aria-label="Ouvrir le menu"
                    >
                        <Menu className="h-5 w-5 dark:text-[#F1F5F9]" />
                    </button>
                    <h1 className="flex-1 min-w-0 text-base font-semibold text-[#1E293B] dark:text-[#F1F5F9] truncate">
                        {pageTitle}
                    </h1>
                </header>

                {/* Mobile Overlay */}
                {sidebarOpen && (
                    <div
                        className="lg:hidden fixed inset-0 bg-black/50 z-40"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <aside
                    className={cn(
                        'fixed top-0 left-0 h-full bg-white dark:bg-[#1E293B] border-r border-[#E2E8F0] dark:border-[#334155] z-50 transition-transform duration-300',
                        'w-70 flex flex-col pl-[env(safe-area-inset-left)]',
                        'lg:translate-x-0',
                        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    )}
                >
                    {/* Logo */}
                    <div className="h-16 px-6 pt-[env(safe-area-inset-top)] flex items-center justify-between border-b border-[#E2E8F0] dark:border-[#334155]">
                        <div className="flex items-center gap-3">
                            <img src="/logo.png" alt="BTPal" className="w-8 h-8 rounded-lg" />
                            <span className="text-xl font-bold text-[#1E293B] dark:text-[#F1F5F9] [font-family:var(--font-display)]">BTPal</span>
                        </div>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden h-11 w-11 -mr-2 flex items-center justify-center rounded hover:bg-[#F1F5F9] dark:hover:bg-[#334155]"
                            aria-label="Fermer le menu"
                        >
                            <X className="h-5 w-5 dark:text-[#F1F5F9]" />
                        </button>
                    </div>

                    {/* User Info */}
                    <div className="px-4 py-4 border-b border-[#E2E8F0] dark:border-[#334155]">
                        <div className="flex items-center gap-3 p-2 rounded-lg bg-[#F8FAFC] dark:bg-[#0F172A]">
                            {user?.logoUrl ? (
                                <img src={user.logoUrl} alt={user.company || ''} className="w-10 h-10 rounded-lg object-contain" />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-[#0F2744] dark:bg-[#2563EB] flex items-center justify-center text-white font-bold">
                                    {user?.company?.charAt(0) || 'N'}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#1E293B] dark:text-[#F1F5F9] truncate">
                                    {user?.company || 'Mon entreprise'}
                                </p>
                                <p className="text-xs text-[#64748B] dark:text-[#94A3B8] truncate">
                                    {user?.email || ''}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-3 overflow-y-auto overscroll-contain">
                        {navGroups.map((group, gi) => {
                            const visibleItems = group.items.filter(
                                (item) => !item.module || user?.[item.module] !== false
                            );
                            if (visibleItems.length === 0) return null;
                            return (
                                <div key={gi} className={gi > 0 ? 'mt-4' : ''}>
                                    {group.label && (
                                        <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#94A3B8]">
                                            {group.label}
                                        </p>
                                    )}
                                    <div className="space-y-0.5">
                                        {visibleItems.map((item) => (
                                            <NavLink
                                                key={item.to}
                                                to={item.to}
                                                onClick={() => setSidebarOpen(false)}
                                                className={({ isActive }) =>
                                                    cn(
                                                        'flex items-center gap-3 px-3 py-2 min-h-11 lg:min-h-0 rounded-lg transition-colors',
                                                        'text-sm font-medium',
                                                        isActive
                                                            ? 'bg-[#0F2744] dark:bg-[#2563EB] text-white'
                                                            : 'text-[#64748B] dark:text-[#94A3B8] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9]'
                                                    )
                                                }
                                            >
                                                <item.icon className="h-4 w-4 shrink-0" />
                                                <span>{item.label}</span>
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </nav>

                    {/* Bottom — Paramètres + Déconnexion */}
                    <div className="px-3 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-[#E2E8F0] dark:border-[#334155] space-y-0.5">
                        <button
                            type="button"
                            onClick={() => {
                                setSidebarOpen(false);
                                window.dispatchEvent(new Event('feedback:open'));
                            }}
                            className="lg:hidden flex items-center gap-3 px-3 py-2 min-h-11 rounded-lg transition-colors text-sm font-medium w-full text-[#64748B] dark:text-[#94A3B8] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9]"
                        >
                            <MessageSquare className="h-4 w-4 shrink-0" />
                            <span>Signaler / Suggérer</span>
                        </button>
                        <NavLink
                            to="/settings"
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                                cn(
                                    'flex items-center gap-3 px-3 py-2 min-h-11 lg:min-h-0 rounded-lg transition-colors text-sm font-medium w-full',
                                    isActive
                                        ? 'bg-[#0F2744] dark:bg-[#2563EB] text-white'
                                        : 'text-[#64748B] dark:text-[#94A3B8] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9]'
                                )
                            }
                        >
                            <Settings className="h-4 w-4 shrink-0" />
                            <span>Paramètres</span>
                        </NavLink>
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEF2F2] dark:hover:bg-[#7F1D1D] py-2 min-h-11 lg:min-h-0 text-sm"
                            onClick={handleLogout}
                        >
                            <LogOut className="h-4 w-4 shrink-0" />
                            <span>Déconnexion</span>
                        </Button>

                        {/* Liens légaux */}
                        <div className="pt-3 mt-1 border-t border-[#E2E8F0] dark:border-[#334155] flex flex-wrap gap-x-3 gap-y-1">
                            <a href="/mentions-legales" className="text-[10px] text-[#94A3B8] hover:text-[#64748B] dark:hover:text-[#F1F5F9]">Mentions légales</a>
                            <a href="/cgu" className="text-[10px] text-[#94A3B8] hover:text-[#64748B] dark:hover:text-[#F1F5F9]">CGU</a>
                            <a href="/politique-confidentialite" className="text-[10px] text-[#94A3B8] hover:text-[#64748B] dark:hover:text-[#F1F5F9]">Confidentialité</a>
                        </div>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="lg:ml-70 min-h-screen pt-[calc(3.5rem+env(safe-area-inset-top))] lg:pt-0 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
                    <DemoBanner />
                    <SystemBanner />
                    {!bannerDismissed && (
                        <TrialBanner
                            subscription={subscription}
                            onDismiss={() => setBannerDismissed(true)}
                            onUpgrade={() => setShowPricing(true)}
                        />
                    )}
                    {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
                    <div className="p-4 lg:p-6">
                        <CoachmarkProvider>{children}</CoachmarkProvider>
                    </div>
                </main>
            </div>
            <FeedbackButton />
            <InstallAppPrompt />
        </>
    );
}

export default Layout;
